import React from "react";
import { QRCodeSVG } from "qrcode.react";
import "./IdCard.css";
import logoDark from "../../assets/images/logo-dark.png";

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const buildMemberId = (user) => {
  const year = user.createdDate
    ? new Date(user.createdDate).getFullYear() || new Date().getFullYear()
    : new Date().getFullYear();
  const seq = user.srNo || 1;
  return `YDS-${year}-${String(seq).padStart(6, "0")}`;
};

const buildAddressLines = (user) => {
  const lines = [];
  if (user.houseNo) lines.push(user.houseNo.trim());
  const streetArea = [user.streetName, user.area].filter(Boolean).join(", ").trim();
  if (streetArea) lines.push(streetArea);
  const cityState = [user.cityMaster, user.stateMaster].filter(Boolean).join(", ");
  const tail = [cityState, user.pincode].filter(Boolean).join(" – ");
  const cityLine = [tail, user.countryMaster].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  return lines.length ? lines : ["N/A"];
};

export const buildTelHref = (phone) => {
  if (!phone) return null;
  const digits = phone.toString().replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

export const buildMapsHref = (user) => {
  const full = [
    user.houseNo,
    user.streetName,
    user.area,
    user.cityMaster,
    user.stateMaster,
    user.countryMaster,
    user.pincode,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (!full) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(full)}`;
};

const IdCard = ({ user }) => {
  const memberId = buildMemberId(user);
  const addressLines = buildAddressLines(user);
  const invitationOn = (user.invitation || "").toLowerCase() === "yes";
  const patrikaOn = (user.patrika || "").toLowerCase() === "yes";
  const telHref = buildTelHref(user.phone);
  const mapsHref = buildMapsHref(user);
  const cardUrl = user._id
    ? `${window.location.origin}/card/${user._id}`
    : window.location.origin;

  return (
    <div className="yds-idcard-stage">
      <div className="yds-idcard-col">
        <div className="yds-idcard-label">Front</div>
        <div className="yds-idcard-card">
          <div className="yds-idcard-lattice" />
          <div className="yds-idcard-band">
            <div className="yds-idcard-brand">
              <img src={logoDark} alt="YDS emblem" />
              <div className="yds-idcard-brand-word">
                <b>YDS</b>
                <span>Member Card</span>
              </div>
            </div>
            <div className="yds-idcard-type">Member</div>
          </div>

          <div className="yds-idcard-front-body">
            <div className="yds-idcard-photo">
              <svg viewBox="0 0 84 100" fill="none">
                <circle cx="42" cy="38" r="17" fill="#eecdb8" />
                <path d="M42 60c-19 0-30 11-30 27v13h60V87c0-16-11-27-30-27Z" fill="#eecdb8" />
              </svg>
            </div>
            <div className="yds-idcard-fields">
              <div className="yds-idcard-name">{user.fullName || "N/A"}</div>
              <div className="yds-idcard-role">Registered Member</div>

              <div className="yds-idcard-field-row">
                <div className="yds-idcard-field-icon">
                  <PhoneIcon />
                </div>
                <div className="yds-idcard-field-text">
                  <span className="yds-idcard-field-label">Phone</span>
                  {telHref ? (
                    <a className="yds-idcard-field-value yds-idcard-link" href={telHref}>
                      {user.phone}
                    </a>
                  ) : (
                    <span className="yds-idcard-field-value">N/A</span>
                  )}
                </div>
              </div>

              <div className="yds-idcard-chips">
                <span className={`yds-idcard-chip${invitationOn ? " on" : ""}`}>
                  <span className="yds-idcard-dot" />
                  Invitation
                </span>
                <span className={`yds-idcard-chip${patrikaOn ? " on" : ""}`}>
                  <span className="yds-idcard-dot" />
                  Pratika
                </span>
              </div>
            </div>
          </div>

          <div className="yds-idcard-member-id-strip">
            <div className="yds-idcard-field-label">Member ID</div>
            <div className="yds-idcard-field-value" style={{ fontSize: 13 }}>
              {memberId}
            </div>
          </div>

        </div>
      </div>

      <div className="yds-idcard-col">
        <div className="yds-idcard-label">Back</div>
        <div className="yds-idcard-card yds-idcard-card-back">
          <div className="yds-idcard-lattice" />
          <div className="yds-idcard-back-top-strip" />

          <div className="yds-idcard-back-content">
            <div className="yds-idcard-back-info">
              <div className="yds-idcard-back-section-label">Registered Address</div>
              {mapsHref ? (
                <a
                  className="yds-idcard-address-block yds-idcard-link"
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {addressLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < addressLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </a>
              ) : (
                <p className="yds-idcard-address-block">
                  {addressLines.map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < addressLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              )}

              <div className="yds-idcard-kv-row">
                <div className="yds-idcard-kv">
                  <span className="yds-idcard-field-label">Email</span>
                  <span className="yds-idcard-field-value">{user.email || "N/A"}</span>
                </div>
                <div className="yds-idcard-kv">
                  <span className="yds-idcard-field-label">Date of Birth</span>
                  <span className="yds-idcard-field-value">{user.dob || "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="yds-idcard-back-qr-col">
              <div className="yds-idcard-qr big">
                <QRCodeSVG
                  value={cardUrl}
                  size={240}
                  bgColor="#ffffff"
                  fgColor="#2b211d"
                  level="M"
                  marginSize={1}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              <span className="yds-idcard-qr-cap">Scan to verify</span>
              <div className="yds-idcard-back-member-id">{memberId}</div>
            </div>
          </div>

          <div className="yds-idcard-legal">
            This card is the property of YDS. If found, please return to the organization.
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdCard;
